import * as UserActions from "src/app/store/user/actions";
import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from "@ngrx/operators";
import { Observable, of, catchError, mergeMap, map, tap, withLatestFrom, first, filter, from, switchMap, concatMap, timer, takeWhile, EMPTY } from "rxjs";
import {
  EngagementFlowWithProgramEngagementFlowDto,
  MinimalPassDto,
  ProgramControllerGetAnonymousCouponApplePassV1RequestParams,
  ProgramControllerGetApplePassV1RequestParams,
  ProgramControllerGetCouponUniqueCodeApplePassV1RequestParams,
  ProgramService,
  AdminService,
} from "src/app/services/api";
import { HttpClient, HttpErrorResponse, HttpResponse, HttpStatusCode } from "@angular/common/http";
import { AlertController, ModalController, NavController } from "@ionic/angular";
import { Action, Store } from "@ngrx/store";
import { AppRoutes, Platform, SendPassSuccessView } from "src/app/enums";
import * as FromRouter from "src/app/store/router/selectors";
import * as FromUser from "src/app/store/user/selectors";
import { InfoComponent, InfoComponentParams } from "src/app/modals/info/info.component";
import { InfoPageParams } from "src/app/pages/info/info.page";
import { ModalLauncherService } from "src/app/services/modal-launcher/modal-launcher.service";
import { BalanceNamePipe } from "src/app/pipes/balance-name/balance-name.pipe";
import { UtilsService } from "src/app/services/utils/utils.service";
import { DownloadedPageParams } from "src/app/pages/downloaded/downloaded.page";
import { TranslateService } from "@ngx-translate/core";
import * as FromApp from "src/app/store/app/selectors";
import { UnauthorizedDownloadPageParams } from "src/app/pages/unauthorized-download/unauthorized-download.page";
import { jwtDecode } from "jwt-decode";
import { TokenPayload } from "src/app/interfaces";
import * as Sentry from "@sentry/capacitor";

import { RootState } from "..";
import { openLinkOnNewTab } from "src/app/utils/open-link-on-new-tab";
import { getError, getErrorParams } from "src/app/utils/get-error";
import { isProfileFormOutdated } from "src/app/utils/profile-form";

/**
 * The API grants the profile form points asynchronously after the form is completed: wait between checks, longer
 * each time (about 4 minutes in total), until they are granted.
 */
export const PROFILE_FORM_REWARD_CHECK_DELAYS_MS: readonly number[] = [5_000, 10_000, 30_000, 60_000, 120_000];

@Injectable()
export class UserEffects {
  private readonly actions$ = inject(Actions);
  private readonly httpClient = inject(HttpClient);
  private readonly navController = inject(NavController);
  private readonly programService = inject(ProgramService);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly modalController = inject(ModalController);
  private readonly utilsService = inject(UtilsService);
  private readonly alertController = inject(AlertController);
  private readonly translateService = inject(TranslateService);
  private readonly adminService = inject(AdminService);
  private readonly modalLauncher = inject(ModalLauncherService);
  private readonly balanceNamePipe = inject(BalanceNamePipe);

  private readonly completeUserProfileFlowSelector = FromApp.selectProgramEngagementFlowByName(
    EngagementFlowWithProgramEngagementFlowDto.NameEnum.CompleteUserProfile,
  );

  public setSentryCustomerContext$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.setCustomerData, UserActions.getCustomerDataSuccess),
        tap(({ customerData: { customer } }) => {
          Sentry.setUser({
            id: customer?.id,
            externalId: customer?.userData?.externalCustomerId,
          });
        }),
      ),
    { dispatch: false },
  );

  public checkCustomerExists$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.checkCustomerExists),
      mergeMap(params =>
        this.programService.programControllerCheckCustomerExistsV1(params).pipe(
          mergeMap(() => of(UserActions.checkCustomerExistsSuccess())),
          catchError((error: HttpErrorResponse) =>
            of(UserActions.checkCustomerExistsFailure({ error, email: params.checkCustomer.email, transactionId: params.transactionId })),
          ),
        ),
      ),
    ),
  );

  public checkCustomerExistsFailure$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.checkCustomerExistsFailure),
        concatLatestFrom(() => [
          this.store.select(FromRouter.selectProgramSlug),
          this.store.select(FromApp.selectProgramId),
        ]),
        tap(([{ error, email, transactionId }, slug, programId]) => {
          let queryParams: Record<string, string> = { email: encodeURIComponent(email) };

          switch (error.status) {
            case HttpStatusCode.NotFound:
            if (transactionId) queryParams = { ...queryParams, transactionId };
              this.navController.navigateForward([slug, AppRoutes.register], { queryParams });
              break;
            case HttpStatusCode.Found:
              this.navController.navigateForward([slug, AppRoutes.passSent], { queryParams });
              this.store.dispatch(UserActions.sendPassToCustomer({ programId, checkCustomer: { email } }));
              break;
          }
        }),
      ),
    { dispatch: false },
  );

  public sendPassToCustomer$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.sendPassToCustomer),
      mergeMap(params =>
        this.programService.programControllerSendPassToCustomerV1(params).pipe(
          mergeMap(() => of(UserActions.sendPassToCustomerSuccess({
            email: params.checkCustomer.email,
            successView: params.successView,
          }))),
          catchError((error: HttpErrorResponse) => of(UserActions.sendPassToCustomerFailure({
            error,
            email: params.checkCustomer.email,
            successView: params.successView,
          }))),
        ),
      ),
    ),
  );

  public sendPassToCustomerSuccess$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.sendPassToCustomerSuccess),
        concatLatestFrom(() => [
          this.store.select(FromRouter.selectProgramSlug),
          this.store.select(FromRouter.selectUrl),
        ]),
        tap(async ([{ email, successView }, programSlug, currentUrl]) => {
          if (successView === SendPassSuccessView.InfoModal) {
            const componentProps: InfoComponentParams = {
              imgSrc: "assets/images/success.svg",
              title: "SEND_PASS_TO_CUSTOMER_SUCCESS_TITLE",
              subtext: "SEND_PASS_TO_CUSTOMER_SUCCESS_SUBTEXT",
              buttonText: "SEND_PASS_TO_CUSTOMER_SUCCESS_BUTTON",
            };
            const dynamicInitialBreakpoint = this.modalLauncher.computeSheetBreakpoint();

            await this.modalLauncher.present({
              component: InfoComponent,
              cssClass: "info-modal",
              componentProps,
              backdropDismiss: true,
              showBackdrop: true,
              mode: "ios",
              breakpoints: [0, dynamicInitialBreakpoint, 0.8],
              initialBreakpoint: dynamicInitialBreakpoint,
              expandToScroll: false,
            });
            return;
          }

          if (!currentUrl?.includes(AppRoutes.passSent)) {
            const queryParams = { email: encodeURIComponent(email) };
            this.navController.navigateForward([programSlug, AppRoutes.passSent], { queryParams });
          }
        }),
      ),
    { dispatch: false },
  );

  public sendPassToCustomerFailure$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.sendPassToCustomerFailure),
        concatLatestFrom(() => [
          this.store.select(FromRouter.selectProgramSlug),
          this.store.select(FromRouter.selectUrl),
        ]),
        tap(([{ email, successView }, slug, currentUrl]) => {
          if (successView === SendPassSuccessView.InfoModal) return;

          if (email && !currentUrl?.includes(AppRoutes.sendPass) && !currentUrl?.includes(AppRoutes.passSent)) {
            const queryParams = { email: encodeURIComponent(email) };
            this.navController.navigateForward([slug, AppRoutes.sendPass], { queryParams });
          }
        }),
      ),
    { dispatch: false },
  );

  public validateCustomer$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.validateCustomer),
      mergeMap(params =>
        this.programService.programControllerValidateCustomerV1(params).pipe(
          mergeMap(() => of(UserActions.validateCustomerSuccess())),
          catchError(error => of(UserActions.validateCustomerFailure({ error }))),
        ),
      ),
    ),
  );

  public validateCustomerSuccess$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.validateCustomerSuccess),
        concatLatestFrom(() => this.store.select(FromRouter.selectProgramSlug)),
        tap(async ([, programSlug]) => {
          const data: InfoPageParams = {
            text: "CUSTOMER_VALIDATION_SUCCESS",
            subtext: "CUSTOMER_VALIDATION_SUCCESS_SUBTEXT",
            imgSrc: "assets/images/success.svg",
          };
          this.navController.navigateForward([programSlug, AppRoutes.info], { state: { data } });
        }),
      ),
    { dispatch: false },
  );

  public validateCustomerFailure$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.validateCustomerFailure),
        filter(({ error }) => (error as HttpErrorResponse)?.status !== HttpStatusCode.Unauthorized),
        concatLatestFrom(() => this.store.select(FromRouter.selectProgramSlug)),
        first(([, programSlug]) => !!programSlug),
        tap(async ([{ error }, programSlug]) => {
          const data: InfoPageParams = {
            text: "GENERAL_ERROR",
            subtext: (error instanceof HttpErrorResponse && error.error.response?.errorType) || "CUSTOMER_VALIDATION_FAILURE",
            imgSrc: "assets/images/warning.svg",
          };
          this.navController.navigateForward([programSlug, AppRoutes.info], { state: { data } });
        }),
      ),
    { dispatch: false },
  );

  public downloadPass$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.downloadPass),
      map(({ platform, params }) => {
        const platformToUse = platform || (this.utilsService.isAppleDevice() ? Platform.ios : Platform.android);
        if (platformToUse === Platform.ios) return UserActions.getApplePass(params);
        else return UserActions.getGooglePass(params);
      }),
    ),
  );

  public downloadPassFailure$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.downloadPassFailure),
        withLatestFrom(this.store.select(FromRouter.selectProgramSlug), this.store.select(FromUser.selectDownloadToken)),
        tap(([{}, programSlug, downloadToken]) => {
          const queryParams: UnauthorizedDownloadPageParams = {};
          try {
            if (downloadToken) {
              const { email } = jwtDecode<TokenPayload>(downloadToken);
              queryParams.email = email;
            }
          } catch {}
          this.navController.navigateForward([programSlug, AppRoutes.unauthorizedDownload], { queryParams });
          return UserActions.setDownloadToken({ token: "" });
        }),
      ),
    { dispatch: false },
  );

  public downloadCouponPass$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.downloadCouponPass),
      map(({ platform, params }) => {
        const platformToUse = platform || (this.utilsService.isAppleDevice() ? Platform.ios : Platform.android);
        if (platformToUse === Platform.ios) return UserActions.getAppleCouponPass(params);
        else return UserActions.getGoogleCouponPass(params);
      }),
    ),
  );

  public downloadAnonymousCouponPass$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.downloadAnonymousCouponPass),
      map(({ platform, params }) => {
        const platformToUse = platform || (this.utilsService.isAppleDevice() ? Platform.ios : Platform.android);
        if (platformToUse === Platform.ios) return UserActions.getAppleAnonymousCouponPass(params);
        else return UserActions.getGoogleAnonymousCouponPass(params);
      }),
    ),
  );

  public register$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.register),
      mergeMap(({ programId, registerCustomer, referral, utmCampaign, utmMedium, utmSource, referralLink }) =>
        this.programService.programControllerRegisterCustomerV1(
          { programId, registerCustomer, referral, utmCampaign, utmMedium, utmSource },
        ).pipe(
            mergeMap(({ accessToken, customerData }) => {
              this.store.dispatch(UserActions.setAuthToken({ token: accessToken }));
              this.store.dispatch(UserActions.setCustomerData({ customerData }));

              if (referralLink) {
                return this.adminService.adminControllerGetEngagementFlowsWithProgramEngagementFlowV1({ programId }).pipe(
                  mergeMap(engagementFlows => {
                    const memberGetMember = engagementFlows.find(
                      engagementFlow => engagementFlow.name === "MEMBER_GET_MEMBER" && engagementFlow.active,
                    );

                    if (memberGetMember?.programEngagementFlows && memberGetMember?.programEngagementFlows[0]?.active) {
                      const programEngagementFlow = memberGetMember.programEngagementFlows[0];

                      if (programEngagementFlow.properties.sendDownloadEmail) {
                        return this.programService
                          .programControllerSendPassToCustomerV1({ programId, checkCustomer: { email: registerCustomer.email } })
                          .pipe(
                            mergeMap(() => of(UserActions.openShopLink({ programId, redirectUrl: referralLink }))),
                            catchError((error: HttpErrorResponse) =>
                              of(
                                UserActions.registerFailure({
                                  error,
                                  params: { programId, registerCustomer, referral, utmCampaign, utmMedium, utmSource },
                                }),
                              ),
                            ),
                          );
                      } else {
                        return of(UserActions.openShopLink({ programId, redirectUrl: referralLink }));
                      }
                    } else {
                      return of(
                        this.utilsService.isAppleDevice() ?
                          UserActions.getApplePass({ programId }) :
                          UserActions.getGooglePass({ programId }),
                      );
                    }
                  }),
                  catchError((error: HttpErrorResponse) =>
                    of(
                      UserActions.registerFailure({
                        error,
                        params: { programId, registerCustomer, referral, utmCampaign, utmMedium, utmSource },
                      }),
                    ),
                  ),
                );
              } else {
                return of(this.utilsService.isAppleDevice() ?
                  UserActions.getApplePass({ programId }) :
                  UserActions.getGooglePass({ programId }),
                );
              }
            }),
            catchError((error: HttpErrorResponse) =>
              of(
                UserActions.registerFailure({
                  error,
                  params: { programId, registerCustomer, referral, utmCampaign, utmMedium, utmSource },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  public registerFailure$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.registerFailure),
        concatLatestFrom(() => this.store.select(FromRouter.selectProgramSlug)),
        tap(async ([{ error, params }, slug]) => {
          if (error?.error?.statusCode === HttpStatusCode.Conflict) {
            this.navController.navigateForward([slug, AppRoutes.sendPass], {
              queryParams: { email: encodeURIComponent(params.registerCustomer.email) },
            });
          } else {
            this.store.dispatch(UserActions.setError({ error }));
          }
        }),
      ),
    { dispatch: false },
  );

  public getApplePass$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getApplePass),
      mergeMap(params =>
        this.programControllerGetApplePass(params).pipe(
          mergeMap((response: Blob) => of(UserActions.getApplePassSuccess({ response, params, kind: MinimalPassDto.KindEnum.StoreCard }))),
          catchError((error: HttpErrorResponse) => of(UserActions.getApplePassFailure({ error }))),
        ),
      ),
    ),
  );

  public getAppleCouponPass$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getAppleCouponPass),
      mergeMap(params =>
        this.programControllerGetCouponUniqueCodeApplePass(params).pipe(
          map((response: Blob) =>
            UserActions.getApplePassSuccess({
              response,
              params,
              kind: MinimalPassDto.KindEnum.Coupon,
              couponCode: params.couponCodeValue,
              couponSlug: params.couponId,
            }),
          ),
          catchError((error: HttpErrorResponse) => of(UserActions.getApplePassFailure({ error }))),
        ),
      ),
    ),
  );

  public getAppleAnonymousCouponPass$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getAppleAnonymousCouponPass),
      mergeMap(params =>
        this.programControllerGetAnonymousCouponApplePass(params).pipe(
          filter(response => response instanceof HttpResponse),
          map((response: HttpResponse<Blob>) =>
            UserActions.getApplePassSuccess({
              response: response.body,
              params,
              kind: MinimalPassDto.KindEnum.Coupon,
              couponCode: params.couponCode,
              couponSlug: params.couponId,
              campaignSlug: params.campaignSlug,
            }),
          ),
          catchError((error: HttpErrorResponse) => of(UserActions.getApplePassFailure({ error }))),
        ),
      ),
    ),
  );

  public getApplePassSuccess$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.getApplePassSuccess),
        concatLatestFrom(() => this.store.select(FromRouter.selectProgramSlug)),
        tap(([{ response, kind, couponCode, couponSlug, campaignSlug }, programSlug]) => {
          const blobPkpass = new Blob([response], { type: "application/vnd.apple.pkpass" });
          const url = window.URL.createObjectURL(blobPkpass);

          const data: DownloadedPageParams = {
            text: "PASS_DOWNLOAD_SUCCESS",
            animation: "success",
            walletPass: {
              url,
              kind,
              couponCode,
              couponSlug,
              campaignSlug,
              platform: Platform.ios,
              name: `${programSlug}-loyalty-wallet-pass-${Date.now()}.pkpass`,
            },
          };

          this.navController.navigateForward([programSlug, AppRoutes.downloaded], { state: { data } });
        }),
      ),
    { dispatch: false },
  );

  public getApplePassFailure$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.getApplePassFailure),
        filter(({ error }) => error?.status !== HttpStatusCode.Unauthorized),
        concatLatestFrom(() => this.store.select(FromRouter.selectProgramSlug)),
        tap(([error, programSlug]) => {
          const data: DownloadedPageParams = {
            text: error?.error?.message ?? "PASS_DOWNLOAD_ERROR",
            animation: "error",
          };
          this.navController.navigateForward([programSlug, AppRoutes.downloaded], { state: { data } });
        }),
      ),
    { dispatch: false },
  );

  public getGooglePass$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getGooglePass),
      mergeMap(params =>
        this.programService.programControllerGetGooglePassV1(params).pipe(
          map(response => UserActions.getGooglePassSuccess({ response: { ...response, kind: MinimalPassDto.KindEnum.StoreCard } })),
          catchError((error: HttpErrorResponse) => of(UserActions.getGooglePassFailure({ error }))),
        ),
      ),
    ),
  );

  public getGoogleCouponPass$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getGoogleCouponPass),
      mergeMap(params =>
        this.programService.programControllerGetCouponUniqueCodeGooglePassV1(params).pipe(
          map(response =>
            UserActions.getGooglePassSuccess({
              response: {
                ...response,
                kind: MinimalPassDto.KindEnum.Coupon,
                couponSlug: params.couponId,
                couponCode: params.couponCodeValue,
              },
            }),
          ),
          catchError((error: HttpErrorResponse) => of(UserActions.getGooglePassFailure({ error }))),
        ),
      ),
    ),
  );

  public getGoogleAnonymousCouponPass$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getGoogleAnonymousCouponPass),
      mergeMap(params =>
        this.programService.programControllerGetAnonymousCouponGooglePassV1(params).pipe(
          map(response =>
            UserActions.getGooglePassSuccess({
              response: {
                ...response,
                kind: MinimalPassDto.KindEnum.Coupon,
                couponSlug: params.couponId,
                couponCode: params.couponCode,
                campaignSlug: params.campaignSlug,
              },
            }),
          ),
          catchError((error: HttpErrorResponse) => of(UserActions.getGooglePassFailure({ error }))),
        ),
      ),
    ),
  );

  public getGooglePassSuccess$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.getGooglePassSuccess),
        concatLatestFrom(() => this.store.select(FromRouter.selectProgramSlug)),
        tap(async ([{ response }, programSlug]) => {
          const data: DownloadedPageParams = {
            animation: "success",
            text: "PASS_DOWNLOAD_SUCCESS",
            walletPass: {
              platform: Platform.android,
              kind: response.kind,
              couponCode: response.couponCode,
              couponSlug: response.couponSlug,
              url: response.googlePayURL,
              campaignSlug: response.campaignSlug,
            },
          };
          this.navController.navigateForward([programSlug, AppRoutes.downloaded], { state: { data } });
        }),
      ),
    { dispatch: false },
  );

  public getGooglePassFailure$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.getGooglePassFailure),
        filter(({ error }) => error?.status !== HttpStatusCode.Unauthorized),
        concatLatestFrom(() => this.store.select(FromRouter.selectProgramSlug)),
        tap(async ([error, programSlug]) => {
          const data: DownloadedPageParams = {
            text: error?.error?.error?.message ?? "PASS_DOWNLOAD_ERROR",
            animation: "error",
          };
          this.navController.navigateForward([programSlug, AppRoutes.downloaded], { state: { data } });
        }),
      ),
    { dispatch: false },
  );

  public getCustomerData$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getCustomerData, UserActions.claimCustomerRewardSuccess),
      concatLatestFrom(() => this.store.select(FromApp.selectProgramId)),
      mergeMap(([, programId]) =>
        this.programService.programControllerGetCustomerDataV1({ programId }).pipe(
          mergeMap(customerData => of(UserActions.getCustomerDataSuccess({ customerData }))),
          catchError(() => of(UserActions.getCustomerDataFailure())),
        ),
      ),
    ),
  );

  public refreshCustomerRewardsAfterClaim$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.claimCustomerRewardSuccess),
      map(() => UserActions.getCustomerRewards()),
    ),
  );

  public refreshCustomerCodesAfterClaim$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.claimCustomerRewardSuccess),
      map(() => UserActions.getCustomerCustomerCodes()),
    ),
  );

  public getCustomerRewards$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getCustomerRewards),
      concatLatestFrom(() => [this.store.select(FromApp.selectProgramId), this.store.select(FromApp.selectSessionLanguage)]),
      mergeMap(([params, programId, xAPPLANGUAGE]) =>
        this.programService.programControllerGetCustomerCouponsV1({ ...params, programId, xAPPLANGUAGE }).pipe(
          mergeMap(rewards => of(UserActions.getCustomerRewardsSuccess({ rewards }))),
          catchError(() => of(UserActions.getCustomerRewardsFailure())),
        ),
      ),
    ),
  );

  public sendValidationEmail$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.sendValidationEmail),
      mergeMap(params =>
        this.programService.programControllerSendValidationEmailV1(params).pipe(
          mergeMap(() => of(UserActions.sendValidationEmailSuccess({ email: params.checkCustomer.email }))),
          catchError(() => of(UserActions.sendValidationEmailFailure())),
        ),
      ),
    ),
  );

  public sendValidationEmailSuccess$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.sendValidationEmailSuccess),
        tap(async () => {
          const componentProps: InfoComponentParams = {
            imgSrc: "assets/images/success.svg",
            title: "SEND_VALIDATION_EMAIL_SUCCESS_TITLE",
            subtext: "SEND_VALIDATION_EMAIL_SUCCESS_SUBTEXT",
            buttonText: "SEND_VALIDATION_EMAIL_SUCCESS_BUTTON",
          };
          const modal = await this.modalController.create({
            cssClass: "info-modal",
            component: InfoComponent,
            breakpoints: [0, 0.5],
            initialBreakpoint: 0.5,
            componentProps,
            canDismiss: true,
            backdropDismiss: true,
            showBackdrop: true,
          });
          await modal.present();
        }),
      ),
    { dispatch: false },
  );

  public updateCustomerPreferences$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateCustomerPreferences),
      mergeMap(({ programId, customerId, updateCustomerPreferences }) =>
        this.programService.programControllerUpdateCustomerPreferencesV1({ programId, customerId, updateCustomerPreferences }).pipe(
          map(customer => UserActions.updateCustomerPreferencesSuccess({ customer })),
          catchError(() => of(UserActions.updateCustomerPreferencesFailure())),
        ),
      ),
    ),
  );

  public updateCustomerProfile$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateCustomerProfile),
      mergeMap(({ programId, customerId, updateCustomerProfile, firstCompletion, source }) =>
        this.programService.programControllerUpdateCustomerProfileV1({ programId, customerId, updateCustomerProfile }).pipe(
          map(customer => UserActions.updateCustomerProfileSuccess({ customer, firstCompletion, source })),
          catchError((error: HttpErrorResponse) => of(UserActions.updateCustomerProfileFailure({ error }))),
        ),
      ),
    ),
  );

  public updateCustomerProfileSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateCustomerProfileSuccess),
      map(() => UserActions.getCustomerData()),
    ),
  );

  public updateCustomerProfileEarningFlowSuccess$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.updateCustomerProfileSuccess),
        filter(({ source }) => source === "earning-flow"),
        concatLatestFrom(() => [
          this.store.select(this.completeUserProfileFlowSelector),
          this.store.select(FromUser.selectWalletConfig),
          this.store.select(FromRouter.selectProgramSlug),
        ]),
        tap(async ([{ firstCompletion }, programEngagementFlow, walletConfig, programSlug]) => {
          const points = programEngagementFlow?.properties?.points;
          const balanceName = this.balanceNamePipe.transform(points, walletConfig);

          const componentProps: InfoComponentParams = firstCompletion
            ? {
              imgSrc: "assets/images/success.svg",
              title: "COMPLETE_PROFILE_PAGE.SUCCESS_FIRST_TITLE",
              subtext: this.translateService.instant("COMPLETE_PROFILE_PAGE.SUCCESS_FIRST_SUBTEXT", { points, balanceName }),
              buttonText: "COMPLETE_PROFILE_PAGE.SUCCESS_BUTTON",
            }
            : {
              imgSrc: "assets/images/success.svg",
              title: "COMPLETE_PROFILE_PAGE.SUCCESS_EDIT_TITLE",
              subtext: "COMPLETE_PROFILE_PAGE.SUCCESS_EDIT_SUBTEXT",
              buttonText: "COMPLETE_PROFILE_PAGE.SUCCESS_BUTTON",
            };

          const dynamicInitialBreakpoint = this.modalLauncher.computeSheetBreakpoint();
          await this.modalLauncher.present({
            component: InfoComponent,
            cssClass: "info-modal",
            componentProps,
            backdropDismiss: true,
            showBackdrop: true,
            mode: "ios",
            breakpoints: [0, dynamicInitialBreakpoint, 0.8],
            initialBreakpoint: dynamicInitialBreakpoint,
            expandToScroll: false,
          });

          if (programSlug) {
            this.navController.navigateBack([programSlug, AppRoutes.loyalty, AppRoutes.engagementFlows]);
          }
        }),
      ),
    { dispatch: false },
  );

  public getProfileForm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getProfileForm),
      switchMap(({ programId }) =>
        this.programService.profileFormControllerGetProfileFormV1({ programId }).pipe(
          map(profileForm => UserActions.getProfileFormSuccess({ profileForm })),
          catchError((error: HttpErrorResponse) => of(UserActions.getProfileFormFailure({ error }))),
        ),
      ),
    ),
  );

  public updateProfileForm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateProfileForm),
      concatLatestFrom(() => this.store.select(FromUser.selectProfileForm)),
      concatMap(([{ programId, updateProfileFormDto }, previous]) =>
        this.programService.profileFormControllerUpdateProfileFormV1({ programId, updateProfileFormDto }).pipe(
          map(profileForm => UserActions.updateProfileFormSuccess({
            profileForm,
            // Points are granted once: re-editing a rewarded form never celebrates them again.
            firstCompletion: profileForm.completed && !previous?.completed && !previous?.rewarded,
          })),
          catchError((error: HttpErrorResponse) => of(UserActions.updateProfileFormFailure({ error }))),
        ),
      ),
    ),
  );

  public reloadOutdatedProfileForm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateProfileFormFailure),
      filter(({ error }) => isProfileFormOutdated(error)),
      concatLatestFrom(() => this.store.select(FromApp.selectProgramId)),
      map(([, programId]) => UserActions.getProfileForm({ programId })),
    ),
  );

  /** Answers linked to the customer profile (name, phone, birthdate) and the earned points change the customer too. */
  public refreshCustomerAfterProfileForm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateProfileFormSuccess),
      map(() => UserActions.getCustomerData()),
    ),
  );

  public waitForProfileFormReward$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateProfileFormSuccess),
      filter(({ profileForm }) => profileForm.completed && !profileForm.rewarded),
      concatLatestFrom(() => this.store.select(FromApp.selectProgramId)),
      switchMap(([, programId]) =>
        from(PROFILE_FORM_REWARD_CHECK_DELAYS_MS).pipe(
          concatMap(ms => timer(ms).pipe(switchMap(() => this.programService.profileFormControllerGetProfileFormV1({ programId })))),
          takeWhile(profileForm => !profileForm.rewarded, true),
          mergeMap(profileForm => profileForm.rewarded
            ? [UserActions.getProfileFormSuccess({ profileForm }), UserActions.getCustomerData()]
            : [UserActions.getProfileFormSuccess({ profileForm })]),
          catchError(() => EMPTY),
        ),
      ),
    ),
  );

  public profileFormFirstCompletion$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.updateProfileFormSuccess),
        filter(({ firstCompletion }) => firstCompletion),
        concatLatestFrom(() => this.store.select(FromUser.selectWalletConfig)),
        tap(async ([{ profileForm: { points } }, walletConfig]) => {
          const balanceName = this.balanceNamePipe.transform(points, walletConfig);
          const componentProps: InfoComponentParams = {
            imgSrc: "assets/images/success.svg",
            title: "COMPLETE_PROFILE_PAGE.SUCCESS_FIRST_TITLE",
            subtext: this.translateService.instant("COMPLETE_PROFILE_PAGE.SUCCESS_FIRST_SUBTEXT", { points, balanceName }),
            buttonText: "COMPLETE_PROFILE_PAGE.SUCCESS_BUTTON",
          };

          const dynamicInitialBreakpoint = this.modalLauncher.computeSheetBreakpoint();
          await this.modalLauncher.present({
            component: InfoComponent,
            cssClass: "info-modal",
            componentProps,
            backdropDismiss: true,
            showBackdrop: true,
            mode: "ios",
            breakpoints: [0, dynamicInitialBreakpoint, 0.8],
            initialBreakpoint: dynamicInitialBreakpoint,
            expandToScroll: false,
          });
        }),
      ),
    { dispatch: false },
  );

  /**
   * Backfill customer.language for customers created outside the register flow
   * (e.g. Shopify sync) so server-side comms have a real language to send in.
   * Fires exactly once per customer — once language is set, the filter never matches again.
   */
  public backfillUserLanguage$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getCustomerDataSuccess),
      filter(({ customerData }) => !!customerData?.customer && !customerData.customer.language),
      concatLatestFrom(() => this.store.select(FromApp.selectProgramId)),
      map(([{ customerData: { customer } }, programId]) =>
        UserActions.updateCustomerPreferences({
          programId,
          customerId: customer.id,
          updateCustomerPreferences: {
            balanceNotifications: customer.balanceNotifications,
            tierNotifications: customer.tierNotifications,
            locationNotifications: customer.locationNotifications,
            marketingNotifications: customer.marketingNotifications,
            rewardAvailableNotifications: customer.rewardAvailableNotifications,
            language: navigator.language,
          },
        }),
      ),
    ),
  );

  public logout$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.logout),
        tap(({ callback }) => {
          Sentry.setUser(null);
          callback();
        }),
      ),
    { dispatch: false },
  );

  public openShopLink$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.openShopLink),
      concatLatestFrom(() => this.store.select(FromApp.selectIsFeatureEnabledByName("multi-pass"))),
      mergeMap(([params, isMultiPassActive]) =>
        isMultiPassActive
          ? this.programService.programControllerGetCustomerMultiPassLinkV1(params).pipe(
            map(links => UserActions.openShopLinkSuccess({ links })),
            catchError(() => of(UserActions.openShopLinkFailure())),
          )
          : of(UserActions.openShopLinkSuccess({ links: [{ link: params.redirectUrl, integration: null }] })),
      ),
    ),
  );

  public openShopLinkSuccess$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.openShopLinkSuccess),
        filter(({ links }) => !!links?.length),
        tap(({ links }) => {
          const redirectTo = links[0].link;

          if (redirectTo?.includes("https") || redirectTo?.includes("http")) openLinkOnNewTab(redirectTo);
        }),
      ),
    { dispatch: false },
  );

  public claimCustomerReward$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.claimCustomerReward),
      mergeMap(({ params, balance, balanceName }) =>
        from(this.showClaimAlert(balance, balanceName)).pipe(
          mergeMap(role =>
            role === "cancel"
              ? of(UserActions.claimCustomerRewardFailure({ error: null }))
              : this.programService.programControllerClaimCustomerRewardV1(params).pipe(
                map(claimedReward => UserActions.claimCustomerRewardSuccess({ claimedReward })),
                catchError((error: HttpErrorResponse) => {
                  if (getError(error) === "POINTS_ON_HOLD") {
                    const { pendingPoints, daysUntilRedeemable } = getErrorParams(error);
                    this.showPointsOnHoldAlert(pendingPoints as number, daysUntilRedeemable as number, balanceName);
                  }

                  return of(UserActions.claimCustomerRewardFailure({ error }));
                }),
              ),
          ),
        ),
      ),
    ),
  );

  public getCustomerTransactions$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getCustomerTransactions),
      concatLatestFrom(() => [this.store.select(FromApp.selectProgramId)]),
      mergeMap(([params, programId]) =>
        this.programService.programControllerGetCustomerTransactionsV1({ ...params, programId, limit: 30, page: 1 }).pipe(
        map(transactions => UserActions.getCustomerTransactionsSuccess({ transactions: transactions.data })),
        catchError(() => of(UserActions.getCustomerTransactionsFailure())),
      )),
    ),
  );

  public getCustomerCoupons$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getCustomerCustomerCodes),
      concatLatestFrom(() => [this.store.select(FromApp.selectProgramId)]),
      mergeMap(([params, programId]) =>
        this.programService.programControllerGetCustomerCouponCodesV1({ ...params, programId, limit: 30, page: 1 }).pipe(
          map((customerCodes) => UserActions.getCustomerCustomerCodesSuccess({ customerCodes: customerCodes.data })),
          catchError(() => of(UserActions.getCustomerCustomerCodesFailure())),
        )),
    ),
  );


  constructor() {}

  private async showClaimAlert(cost: number, balanceName: string): Promise<string> {
    const alert = await this.alertController.create({
      header: this.translateService.instant(cost > 0 ? "CLAIM_HEADER" : "CLAIM_HEADER_NO_COST", { cost, balanceName }),
      message: this.translateService.instant(cost > 0 ? "CLAIM_MESSAGE" : "CLAIM_MESSAGE_NO_COST"),
      buttons: [
        {
          role: "cancel",
          text: this.translateService.instant("CLAIM_CANCEL"),
        },
        {
          text: this.translateService.instant("CLAIM_CONFIRM"),
        },
      ],
    });

    await alert.present();
    const { role } = await alert.onDidDismiss();
    return role;
  }

  private async showPointsOnHoldAlert(pendingPoints: number, daysUntilRedeemable: number, balanceName: string): Promise<void> {
    const alert = await this.alertController.create({
      header: this.translateService.instant("REWARD_PAGE.POINTS_ON_HOLD.HEADER"),
      message: this.translateService.instant("REWARD_PAGE.POINTS_ON_HOLD.MESSAGE", { pendingPoints, daysUntilRedeemable, balanceName }),
      buttons: [{ text: this.translateService.instant("REWARD_PAGE.POINTS_ON_HOLD.OK") }],
    });

    await alert.present();
  }

  private programControllerGetApplePass(
    requestParameters: ProgramControllerGetApplePassV1RequestParams,
    observe?: "body",
    reportProgress?: boolean,
    options?: { httpHeaderAccept?: undefined },
  ): Observable<unknown> {
    const programId = requestParameters.programId;

    if (programId === null || programId === undefined) {
      throw new Error("Required parameter programId was null or undefined when calling programControllerGetApplePass.");
    }

    let headers = this.programService.defaultHeaders;

    let credential: string | undefined;
    // authentication (bearer) required
    credential = this.programService.configuration.lookupCredential("bearer");

    if (credential) {
      headers = headers.set("Authorization", "Bearer " + credential);
    }

    let httpHeaderAcceptSelected: string | undefined = options?.httpHeaderAccept;

    if (httpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts: string[] = [];
      httpHeaderAcceptSelected = this.programService.configuration.selectHeaderAccept(httpHeaderAccepts);
    }

    if (httpHeaderAcceptSelected !== undefined) {
      headers = headers.set("Accept", httpHeaderAcceptSelected);
    }

    // to determine the Content-Type header
    const consumes: string[] = ["application/json"];

    const httpContentTypeSelected: string | undefined = this.programService.configuration.selectHeaderContentType(consumes);

    if (httpContentTypeSelected !== undefined) {
      headers = headers.set("Content-Type", httpContentTypeSelected);
    }

    return this.httpClient.get(
      `${this.programService.configuration.basePath}/v1/programs/${encodeURIComponent(String(programId))}/apple/pass`,
      {
        responseType: "blob",
        withCredentials: this.programService.configuration.withCredentials,
        headers: headers,
        observe: observe,
        reportProgress: reportProgress,
      },
    );
  }

  private programControllerGetCouponUniqueCodeApplePass(
    requestParameters: ProgramControllerGetCouponUniqueCodeApplePassV1RequestParams,
    observe?: "body",
    reportProgress?: boolean,
    options?: { httpHeaderAccept?: undefined },
  ): Observable<unknown> {
    const programId = requestParameters.programId;
    if (programId === null || programId === undefined) {
      throw new Error("Required parameter programId was null or undefined when calling programControllerGetCouponUniqueCodeApplePass.");
    }
    const couponId = requestParameters.couponId;
    if (couponId === null || couponId === undefined) {
      throw new Error("Required parameter couponId was null or undefined when calling programControllerGetCouponUniqueCodeApplePass.");
    }
    const couponCodeValue = requestParameters.couponCodeValue;
    if (couponCodeValue === null || couponCodeValue === undefined) {
      throw new Error(
        "Required parameter couponCodeValue was null or undefined when calling programControllerGetCouponUniqueCodeApplePass.",
      );
    }

    let headers = this.programService.defaultHeaders;

    let credential: string | undefined;
    // authentication (bearer) required
    credential = this.programService.configuration.lookupCredential("bearer");
    if (credential) {
      headers = headers.set("Authorization", "Bearer " + credential);
    }

    let httpHeaderAcceptSelected: string | undefined = options?.httpHeaderAccept;
    if (httpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts: string[] = [];
      httpHeaderAcceptSelected = this.programService.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (httpHeaderAcceptSelected !== undefined) {
      headers = headers.set("Accept", httpHeaderAcceptSelected);
    }

    return this.httpClient.get(
      `${this.programService.configuration.basePath}/v1/programs/${encodeURIComponent(String(programId))}/coupons/${encodeURIComponent(String(couponId))}/codes/${encodeURIComponent(String(couponCodeValue))}/apple/pass`,
      {
        responseType: "blob",
        withCredentials: this.programService.configuration.withCredentials,
        headers: headers,
        observe: observe,
        reportProgress: reportProgress,
      },
    );
  }

  private programControllerGetAnonymousCouponApplePass(
    requestParameters: ProgramControllerGetAnonymousCouponApplePassV1RequestParams,
    reportProgress: boolean = false,
  ): Observable<unknown> {
    const programId = requestParameters.programId;
    if (programId === null || programId === undefined) {
      throw new Error("Required parameter programId was null or undefined when calling programControllerGetAnonymousCouponApplePass.");
    }
    const couponId = requestParameters.couponId;
    if (couponId === null || couponId === undefined) {
      throw new Error("Required parameter couponId was null or undefined when calling programControllerGetAnonymousCouponApplePass.");
    }
    const campaignSlug = requestParameters.campaignSlug;
    if (campaignSlug === null || campaignSlug === undefined) {
      throw new Error("Required parameter campaignSlug was null or undefined when calling programControllerGetAnonymousCouponApplePass.");
    }

    const headers = this.programService.defaultHeaders;

    return this.httpClient
      .get(
        `${this.programService.configuration.basePath}/v1/programs/${encodeURIComponent(String(programId))}/anonymous-coupons/${encodeURIComponent(String(couponId))}/apple/pass`,
        {
          params: {
            "campaign-slug": requestParameters.campaignSlug,
            "coupon-code": requestParameters.couponCode,
          },
          responseType: "blob",
          withCredentials: this.programService.configuration.withCredentials,
          headers: headers,
          observe: "events",
          reportProgress: reportProgress,
        },
      )
      .pipe(catchError(this.parseErrorBlob));
  }


  private parseErrorBlob(error: HttpErrorResponse): any {
    const reader: FileReader = new FileReader();
    const observable = new Observable((observer: any) => {
      reader.onloadend = (): void => {
        observer.error(JSON.parse(reader.result as any));
        observer.complete();
      };
    });
    reader.readAsText(error.error);
    return observable;
  }

}
